import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  parsePaymentUrlParams,
  validateRequiredParams,
  determineStartPage,
  type PaymentUrlParams
} from '../lib/urlParams';

interface PaymentRouterProps {
  children?: React.ReactNode;
}

/**
 * PaymentRouter
 *
 * Handles URL parameter parsing and screen skipping logic
 * Redirects to the appropriate starting page based on provided params
 */
export function PaymentRouter({ children }: PaymentRouterProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setParams] = useState<PaymentUrlParams | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    initializeRouter();
  }, [location.pathname, location.search]);

  async function initializeRouter() {
    try {
      const searchParams = new URLSearchParams(location.search);
      const storedParams = sessionStorage.getItem('pouchpay_params');

      // If already initialized and navigating to internal page without query params
      // Just use stored params and don't re-validate
      if (initialized && !searchParams.has('source') && !searchParams.has('signature')) {
        if (storedParams) {
          const parsedParams = JSON.parse(storedParams);
          setParams(parsedParams);
          setLoading(false);
          return;
        }
      }

      // Parse URL params from query string
      const urlParams = parsePaymentUrlParams(searchParams);

      // Validate required params (source + signature)
      const validation = validateRequiredParams(urlParams);

      if (!validation.valid) {
        // If validation fails but we have stored params, use them (internal navigation)
        if (storedParams) {
          const parsedParams = JSON.parse(storedParams);
          setParams(parsedParams);
          setLoading(false);
          return;
        }

        // No stored params and invalid URL - show error
        setError(validation.error || 'Invalid payment URL');
        setLoading(false);
        return;
      }

      // Valid params - store them
      sessionStorage.setItem('pouchpay_params', JSON.stringify(urlParams));

      // Check for access token in URL or localStorage
      let accessToken = urlParams.at;
      if (!accessToken) {
        const stored = localStorage.getItem('pouchpay_token');
        if (stored) {
          accessToken = stored;
        }
      } else {
        // Store token from URL for future use
        localStorage.setItem('pouchpay_token', accessToken);
      }

      // Update params with token
      if (accessToken) {
        urlParams.at = accessToken;
        // Update stored params with token
        sessionStorage.setItem('pouchpay_params', JSON.stringify(urlParams));
      }

      setParams(urlParams);
      setInitialized(true);

      // Determine starting page based on params
      const startPage = determineStartPage(urlParams);

      // Only redirect if we're on the base /pay route
      if (location.pathname === '/pay' && startPage !== '/pay') {
        navigate(startPage, { replace: true });
      }

      setLoading(false);

    } catch (err: any) {
      console.error('Router initialization error:', err);
      setError(err.message || 'Failed to initialize payment flow');
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-4" />
          <p className="text-gray-600">Initializing payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Invalid Payment Link
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="https://pouchfinance.xyz"
            className="inline-flex items-center gap-2 text-brand hover:text-brand-600 font-medium"
          >
            Go to PouchPay
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Hook to access payment URL params from any page
 * Reads synchronously from sessionStorage to avoid hydration issues
 */
export function usePaymentParams(): PaymentUrlParams | null {
  const [params, setParams] = useState<PaymentUrlParams | null>(() => {
    // Initialize synchronously from sessionStorage
    const stored = sessionStorage.getItem('pouchpay_params');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (err) {
        console.error('Failed to parse stored params:', err);
        return null;
      }
    }
    return null;
  });

  useEffect(() => {
    // Also listen for changes
    const handleStorageChange = () => {
      const stored = sessionStorage.getItem('pouchpay_params');
      if (stored) {
        try {
          setParams(JSON.parse(stored));
        } catch (err) {
          console.error('Failed to parse stored params:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return params;
}

/**
 * Hook to update payment params (e.g., after user inputs data)
 */
export function useUpdatePaymentParams() {
  return (updates: Partial<PaymentUrlParams>) => {
    const stored = sessionStorage.getItem('pouchpay_params');
    if (stored) {
      try {
        const current = JSON.parse(stored);
        const updated = { ...current, ...updates };
        sessionStorage.setItem('pouchpay_params', JSON.stringify(updated));
      } catch (err) {
        console.error('Failed to update params:', err);
      }
    }
  };
}
